import type { AppLocale } from './types';

export interface PkgItem {
  id: string;
  name: string;
  description: string;
  priceTL: number;
  priceUSD: number;
  unit: string;
  badge: string;
  ctaLabel: string;
  features: string[];
  active: boolean;
  highlight: boolean;
  purchasable: boolean;

  /* Donanım/abonelik ayrışması (EP-07 · §9.1). Backend `PricingPackage` ile aynı alanlar —
   * `/api/config/public` bu şekilde dönüyor, buradaki kopya yalnız API'ye ulaşılamadığında
   * kullanılan yedek metindir. `hardwareFeeTL` PAKET başına sabittir, etiket adediyle çarpılmaz. */
  hardwareFeeTL?: number;
  renewalPriceTL?: number;
  includedStickerCount?: number;
  shippingFeeTL?: number;
}

export interface LandingContent {
  nav: { href: string; label: string }[];
  meta: {
    title: string;
    description: string;
    ogLocale: string;
    ogTitle: string;
    ogDescription: string;
    twitterTitle: string;
    twitterDescription: string;
    heroImageAlt: string;
  };
  navUi: { login: string; demo: string; menuAria: string };
  hero: {
    badge: string;
    h1: string[];
    subtitle: string;
    tryFree: string;
    androidFor: string;
    androidDownload: string;
    iosFor: string;
    iosDownload: string;
    howItWorks: string;
    appsSoon: string;
    scroll: string;
  };
  stats: { value: string; label: string }[];
  howItWorks: { badge: string; title: string; subtitle: string; steps: { num: string; title: string; desc: string }[] };
  features: {
    badge: string;
    titlePrefix: string;
    subtitle: string;
    corporateBadge: string;
    corporateTitle: string;
    corporateTag: string;
    items: { title: string; desc: string; isCorporate?: boolean }[];
  };
  alerts: {
    badge: string;
    titlePrefix: string;
    subtitle: string;
    items: { title: string; desc: string; priority: string }[];
  };
  security: {
    badge: string;
    titlePrefix: string;
    titleHighlight: string;
    subtitle: string;
    bars: { label: string; val: string; pct: number }[];
    badges: string[];
  };
  roadmap: {
    badge: string;
    titlePrefix: string;
    soon: string;
    live: string;
    items: { title: string; desc: string; live?: boolean }[];
  };
  store: {
    badge: string;
    title: string;
    subtitle: string;
    cta: string;
    bullets: string[];
  };
  reviews: {
    badge: string;
    titlePrefix: string;
    countSuffix: string;
    anonymousUser: string;
  };
  pricing: {
    badge: string;
    titlePrefix: string;
    subtitle: string;
    free: string;
    getQuote: string;
  };
  /* Doküman 48 §11 K4: fiyat kartları kalktı; üyelik uygulama içinden, kurumsal teklifle. */
  membership: {
    badge: string;
    title: string;
    subtitle: string;
    individualTitle: string;
    individualDesc: string;
    download: string;
    corporateTitle: string;
    corporateDesc: string;
    corporateCta: string;
  };
  faq: {
    badge: string;
    title: string;
    subtitle: string;
    items: { q: string; a: string }[];
  };
  demo: {
    badge: string;
    titlePrefix: string;
    titleHighlight: string;
    subtitle: string;
    scanHint: string;
    demoQrAlt: string;
    phoneAlt: string;
    qrNote: string;
    openLink: string;
  };
  footer: {
    tagline: string;
    subtitle: string;
    product: string;
    legal: string;
    productLinks: { href: string; label: string }[];
    legalLinks: { href: string; label: string }[];
    /* Satıcı kimliği: PayTR canlı mod ve mesafeli satış mevzuatı unvan/adres/iletişimin
     * sitede erişilebilir olmasını ister; footer her sayfada olduğu için burada. */
    seller: { title: string; name: string; address: string; contact: string };
    copyright: string;
    serviceActive: string;
  };
  checkout: {
    corporateSuccess: string;
    paymentTitle: string;
    corporateTitle: string;
    corporateProcess: string;
  };
  cookie: {
    title: string;
    desc: string;
    accept: string;
    reject: string;
    settings: string;
  };
  downloadModal: {
    title: string;
    desc: string;
    close: string;
  };
  defaultPackages: PkgItem[];
  jsonLd: {
    orgDescription: string;
    appDescription: string;
    offers: { name: string; price: string; description?: string }[];
  };
}

const TR: LandingContent = {
  nav: [
    { href: '#how-it-works', label: 'Nasıl Çalışır' },
    { href: '#features', label: 'Özellikler' },
    { href: '#security', label: 'Güvenlik' },
    { href: '#yorumlar', label: 'Yorumlar' },
    { href: '#faq', label: 'SSS' },
    { href: '#demo', label: 'Demo' },
    { href: '/magaza', label: 'Mağaza' },
    { href: '/iletisim', label: 'İletişim' },
  ],
  meta: {
    title: 'ParkMesaj — Anonim & Güvenli Araç İletişim Sistemi',
    description: 'ParkMesaj: QR kod ile anonim araç uyarısı. Telefon numarası paylaşmadan, uygulama indirmeden anlık bildirim. Mobil Bildirim + SMS ile geri bildirim, KVKK uyumlu şifreleme.',
    ogLocale: 'tr_TR',
    ogTitle: 'ParkMesaj — Anonim & Güvenli Araç Uyarı Sistemi',
    ogDescription: 'Araç sahibini anonim olarak uyarmak hiç bu kadar kolay olmamıştı. QR tarat, hazır şablondan seç, anında bildirim gönder.',
    twitterTitle: 'ParkMesaj — Anonim & Güvenli Araç Uyarı Sistemi',
    twitterDescription: 'QR kod ile anonim araç uyarısı. Uygulama indirmeden, numara paylaşmadan anlık bildirim.',
    heroImageAlt: 'Aracının camına yapıştırılan ParkMesaj QR sticker\'ı tarandığında telefona gelen güvenli park uyarısı bildirimi',
  },
  navUi: { login: 'Giriş Yap', demo: 'Demo Gör', menuAria: 'Menüyü aç/kapat' },
  hero: {
    badge: 'Anonim · Hızlı · Güvenli',
    h1: ['Aracınızdaki', 'Sorunlardan', 'Anında Haberiniz', 'Olsun.'],
    subtitle: 'Telefon numaranızı paylaşmadan, mobil uygulama yükleme zorunluluğu olmadan ve kimliğiniz tamamen gizli kalarak — aracınızla ilgili acil durumlardan saniyeler içinde haberdar olun.',
    tryFree: 'Demoyu Dene',
    androidFor: 'Android için',
    androidDownload: 'Google Play',
    iosFor: 'iOS için',
    iosDownload: 'App Store',
    howItWorks: 'Nasıl Çalışır?',
    appsSoon: 'Mobil uygulamalar App Store ve Google Play\'de çok yakında',
    scroll: 'Kaydır',
  },
  stats: [
    { value: '3 sn', label: 'Ortalama İletim Süresi' },
    { value: '%100', label: 'Anonim & Güvenli İletişim' },
    { value: '0', label: 'Uygulama İndirme Zorunluluğu Yok (Bildiren İçin)' },
  ],
  howItWorks: {
    badge: 'Süreç',
    title: '3 Adımda Anında Uyarı',
    subtitle: 'Kurulum bir kez, koruma sürekli.',
    steps: [
      { num: '01', title: 'Stikeri Aracınıza Yapıştırın', desc: 'QR kodlu stikerinizi aracınızın ön camına yapıştırın. Tek seferlik kolay kurulum ile ömür boyu kullanın.' },
      { num: '02', title: 'QR Kodun Okutulması', desc: 'Aracınızı fark eden kişi hiçbir uygulama indirmeden, telefon kamerasıyla QR kodu taratır ve hazır şablonlardan birini seçer.' },
      { num: '03', title: 'Anında Bildirim Alın', desc: 'Gönderilen ikaz önce mobil uygulamanıza veya web panelinize ulaşır. 1 dakika içinde okunmazsa otomatik SMS olarak telefonunuza iletilir.' },
    ],
  },
  features: {
    badge: 'Özellikler',
    titlePrefix: 'Neden',
    subtitle: 'Gizlilik, hız ve güvenlik — hepsini aynı anda, hiçbir ödün vermeden.',
    corporateBadge: 'Kurumsal Üyeler',
    corporateTitle: 'Filo & Şirket Yönetim Özellikleri',
    corporateTag: 'Kurumsal Filo',
    items: [
      { title: 'Tam Gizlilik ve Anonimlik', desc: 'Telefon numaranız, adınız veya kişisel verileriniz hiçbir şekilde bildiren kişiyle paylaşılmaz. İletişim tamamen gizli kalır.' },
      { title: 'Hızlı ve Kolay Bildirim', desc: 'QR kodu taratan kişinin herhangi bir uygulama yüklemesi veya üye olması gerekmez. Saniyeler içinde uyarısını gönderir.' },
      { title: 'Akıllı Bildirim + Yedek SMS', desc: 'Bildirimler öncelikle push/web bildirimi olarak gelir. Eğer 1 dakika içinde okunmazsa, sistem otomatik olarak yedek SMS gönderir.' },
      { title: 'Fotoğraflı Kanıt Sistemi', desc: 'Bildirime isteğe bağlı olarak fotoğraf eklenebilir. Fotoğraflardaki EXIF (konum, cihaz) bilgileri otomatik temizlenir ve 24 saat sonra kalıcı olarak silinir.' },
      { title: 'KVKK ve Yasal Uyum', desc: 'AES-256-GCM veri şifrelemesi, EXIF temizleme ve otomatik veri imha periyotları sayesinde KVKK standartlarına tam uyumludur.' },
      { title: 'Çoklu Araç Desteği', desc: 'Bireysel pakette 2, Aile paketinde 3 aracınızı tek hesaptan yönetebilir, her araç için ayrı QR kodlu stiker oluşturabilirsiniz.' },
      { title: 'Dinamik Sürücü Atama', desc: 'Filo araçlarınıza ad ve telefon ile anında sürücü tanımlayın. Sürücü değiştiğinde yetkiler ve yönlendirmeler gecikmesiz güncellenir.', isCorporate: true },
      { title: 'Toplu A6 PDF Çıktısı', desc: 'Tüm şirket filonuzun stickerlarını tek tıkla A6 dikey formatta birleştirilmiş tek bir PDF olarak indirip toplu baskı alabilirsiniz.', isCorporate: true },
      { title: 'Akıllı Uyarı Yönlendirme', desc: 'QR kodlar taratıldığında uyarılar şirket sahibine değil, o an araca atanmış olan sürücünün uygulamasına ve telefonuna (SMS) iletilir.', isCorporate: true },
      { title: 'Filo Raporları', desc: 'Kurumsal panelden araç bazlı uyarı istatistiklerini ve aylık trendleri anında görüntüleyin.', isCorporate: true },
    ],
  },
  alerts: {
    badge: 'Bildirim Şablonları',
    titlePrefix: '4 Hazır',
    subtitle: 'Uyarıcı QR\'ı taradıktan sonra sadece bir butona dokunur. Serbest metin yok, hakaret yok.',
    items: [
      { title: 'Hatalı Park Uyarısı', desc: '"Aracınız hatalı park edilmiş veya geçişi engelliyor, kontrol edebilir misiniz?"', priority: 'Normal Öncelik' },
      { title: 'Araç Başı Sorun İkazı', desc: '"Aracınızın farları açık kalmış, camı açık veya lastiği inmiş görünüyor."', priority: 'Normal Öncelik' },
      { title: 'Hafif Hasar / Temas İkazı', desc: '"Aracınıza hafif bir temas veya sürtme gerçekleşti, görüşebilir miyiz?"', priority: 'Yüksek Öncelik' },
      { title: 'Acil Durum Bildirimi', desc: '"Çekici faaliyeti, hırsızlık şüphesi veya yangın gibi acil bir durum var!"', priority: 'KRİTİK' },
    ],
  },
  security: {
    badge: 'Güvenlik & KVKK',
    titlePrefix: 'Gizlilik',
    titleHighlight: 'Tasarımımızın Merkezinde',
    subtitle: 'Sistemimiz "Zero-Trust" (Sıfır Güven) modeli esas alınarak tasarlanmıştır. Kişisel verileriniz asla üçüncü taraflarla paylaşılmaz, gelişmiş algoritmalarla şifrelenmiş olarak tutulur ve belirlenen süre sonunda sistemden kalıcı olarak silinir.',
    bars: [
      { label: 'Telefon Numarası Şifreleme', val: 'AES-256-GCM', pct: 100 },
      { label: 'Fotoğrafların Otomatik Silinmesi', val: '24 saat', pct: 85 },
      { label: 'EXIF Verisi Temizleme', val: '%100', pct: 100 },
      { label: 'Çift Taraflı Kimlik Gizliliği', val: 'Tam', pct: 100 },
    ],
    badges: ['AES-256-GCM Şifreleme', '24 Saat Sonra Otomatik Silme', 'EXIF Konum Verisi Temizleme', 'Sıfır Kimlik Paylaşımı', 'KVKK Standartlarına Uyum', 'Bulut Tabanlı Bot Koruması'],
  },
  roadmap: {
    badge: 'Yol Haritası',
    titlePrefix: 'Çok Daha Fazlası',
    soon: 'Yakında',
    live: 'Yayında',
    items: [
      { title: 'Plaka ile Sorgulama', desc: 'QR olmasa bile plaka üzerinden kayıtlı araç sahibine anonim uyarı.' },
      { title: '15 dk Anonim Chat', desc: 'Uyarıcı ve araç sahibi arasında geçici, anonim ve güvenli sohbet odası.', live: true },
      { title: 'Çekici Uyarı Ağı', desc: 'Çekilmekte olan araç için bölgedeki diğer kullanıcıları anında uyar.' },
      { title: 'Mini Mağaza', desc: 'Dayanıklı QR etiket ve 3D baskı cam tutucu kiti — parkmesaj.com/magaza veya uygulama içinden sipariş verin.', live: true },
    ],
  },
  store: {
    badge: 'Mağaza',
    title: 'Cam etiketi koleksiyonu',
    subtitle: 'QR kartınızı ön cama sabitleyen 3D baskı tutucular. 10 tasarım, 4 gövde rengi; kutudan hazır kartla çıkar, vantuzla takılır. Kargo dahil, her kit alana 20 SMS hediye.',
    cta: 'Koleksiyonu gör',
    bullets: ['Siparişle üretim, 3-5 iş gününde kargoda', 'Kadın, modifiye ve premium segmentleri', 'Hesap açmadan, üç adımda sipariş'],
  },
  reviews: {
    badge: 'Gerçek Kullanıcılar',
    titlePrefix: 'Kullanıcılar Ne',
    countSuffix: 'değerlendirme',
    anonymousUser: 'Anonim Kullanıcı',
  },
  pricing: {
    badge: 'Fiyatlandırma',
    titlePrefix: 'Bütçenize Uygun,',
    subtitle: 'Yıllık üyelik modeliyle bütçenizi zorlamadan sistemi kesintisiz kullanın. Gizli ücretler, sürpriz maliyetler yok.',
    free: 'Ücretsiz',
    getQuote: 'Teklif Alın',
  },
  membership: {
    badge: 'Üyelik',
    title: 'Nasıl Başlarım?',
    subtitle: 'Bireysel yıllık üyelik doğrudan ParkMesaj uygulamasının içinden, App Store veya Google Play hesabınızla alınır. Etiket ve tutucu kitini de uygulamadaki mağazadan sipariş edebilirsiniz.',
    individualTitle: 'Bireysel Kullanıcılar',
    individualDesc: 'Uygulamayı indirin, aracınızı ekleyin, QR etiketinizi basın ya da dayanıklı etiket + tutucu kitini sipariş edin. Yıllık üyelik uygulama içinden başlatılır; ilk 2 hafta ücretsizdir.',
    download: 'Uygulamayı İndir',
    corporateTitle: 'Filolar ve Şirketler',
    corporateDesc: 'Sürücü atama, toplu etiket baskısı ve raporlama içeren kurumsal çözüm için bize ulaşın; ihtiyacınıza göre teklif hazırlayalım.',
    corporateCta: 'Kurumsal Teklif Al',
  },
  faq: {
    badge: 'Sık Sorulan Sorular',
    title: 'Merak Ettikleriniz',
    subtitle: 'ParkMesaj hakkında en çok sorulan sorular — arama motorları ve yapay zeka asistanları için net yanıtlar.',
    items: [
      { q: 'ParkMesaj nedir?', a: 'ParkMesaj, aracınıza yapıştırılan QR kod üzerinden anonim ve güvenli uyarı ileten bir sistemdir. Telefon numaranız camda görünmez; bildiren kişi uygulama indirmeden hazır şablonlarla saniyeler içinde uyarı gönderebilir.' },
      { q: 'QR kodu okutan kişinin kimliği veya konumu paylaşılır mı?', a: 'Hayır. Uyarı tamamen anonimdir; bildiren kişinin telefon numarası veya kimliği araç sahibine iletilmez. Konum bilgisi toplanmaz; isteğe bağlı kanıt fotoğraflarındaki EXIF (GPS) verileri otomatik silinir.' },
      { q: 'Uyarı göndermek için uygulama indirmek zorunlu mu?', a: 'Bildiren kişi için hayır — telefon kamerasıyla QR taratıp web üzerinden uyarı gönderebilir. Araç sahibi bildirimleri almak için mobil uygulama veya web paneli kullanabilir.' },
      { q: 'Üyelik nasıl alınır, neler dahil?', a: 'Yıllık üyelik ParkMesaj uygulamasının içinden App Store veya Google Play hesabınızla alınır; ilk 2 hafta ücretsizdir. Üyelik 2 araca kadar kayıt, QR etiket PDF çıktısı, sınırsız push bildirimi, okunmayan bildirimlere yedek SMS (aylık 10) ve kasko/sigorta/muayene hatırlatıcılarını içerir. Fiziksel etiket + tutucu kiti ayrıca uygulamadaki mağazadan sipariş edilir.' },
      { q: 'Yedek SMS nasıl çalışır?', a: 'Uyarı önce push bildirimi ve web paneline gider. 1 dakika içinde okunmazsa sistem otomatik olarak yedek SMS gönderir ve araç sahibine kısa metin iletilir.' },
      { q: 'ParkMesaj KVKK uyumlu mu?', a: 'Evet. Kişisel veriler AES-256-GCM ile şifrelenir, fotoğraf metadata temizlenir, belirlenen sürelerde otomatik imha uygulanır. Detaylar yasal sayfalarımızda yer alır.' },
      { q: 'Kurumsal filo yönetimi destekleniyor mu?', a: 'Evet. Filo çözümü, araç sayınıza göre teklifle sunulur: dinamik sürücü atama, sürücüye özel push/SMS yönlendirme, toplu A6 sticker PDF ve dashboard raporları içerir. Teklif için ana sayfadaki Kurumsal Teklif formunu kullanabilirsiniz.' },
    ],
  },
  demo: {
    badge: 'Canlı Demo',
    titlePrefix: 'Şimdi',
    titleHighlight: 'Deneyin',
    subtitle: 'Aşağıdaki test QR kodunu telefonunuzun kamerasıyla taratarak uyarı gönderme ekranını anında deneyimleyebilirsiniz. Herhangi bir ek uygulama yüklemeniz gerekmez.',
    scanHint: 'Telefonla Tarat',
    demoQrAlt: 'Demo QR Kodu',
    phoneAlt: 'QR taratıldığında açılan anonim uyarı gönderme ekranı',
    qrNote: '— Demo araç için test QR kodu',
    openLink: 'Demo Bağlantısını Aç',
  },
  footer: {
    tagline: 'Araç sahibini anonim ve güvenli şekilde uyarmak için tasarlanmış akıllı iletişim ekosistemi.',
    subtitle: 'Anonim & Güvenli Araç Uyarı Sistemi',
    product: 'Ürün',
    legal: 'Yasal',
    productLinks: [
      { href: '#how-it-works', label: 'Nasıl Çalışır' },
      { href: '#features', label: 'Özellikler' },
      { href: '#security', label: 'Güvenlik' },
      { href: '#faq', label: 'SSS' },
      { href: '#demo', label: 'Demo' },
      { href: '/iletisim', label: 'İletişim' },
    ],
    legalLinks: [
      { href: '/yasal/kvkk', label: 'KVKK Aydınlatma Metni' },
      { href: '/yasal/sozlesme', label: 'Mesafeli Satış Sözleşmesi' },
      { href: '/yasal/gizlilik', label: 'Gizlilik ve Çerez Politikası' },
      { href: '/yasal/iade', label: 'İptal ve İade Koşulları' },
      { href: '/yasal/sozlesme#teslimat', label: 'Teslimat ve Kargo Koşulları' },
    ],
    seller: {
      title: 'Satıcı',
      name: 'Elektrobil Bilişim Yazılım Elektronik Ar-Ge Reklam ve Gıda San. Tic. Ltd. Şti.',
      address: 'Koca Sinan Mah. Sadık Ahmet Cad. Ahmet Ekşioğlu Apt. No:34 İç Kapı No:19, Merkez / Edirne 22030',
      contact: 'destek@parkmesaj.com · +90 544 365 85 25 · VKN 3310506351',
    },
    copyright: 'Tüm hakları saklıdır.',
    serviceActive: 'Servis Aktif — parkmesaj.com',
  },
  checkout: {
    corporateSuccess: 'Kurumsal teklif talebiniz başarıyla kaydedilmiştir. Satış ekibimiz en kısa sürede sizinle iletişime geçecektir.',
    paymentTitle: 'Ödeme Bilgileri',
    corporateTitle: 'Kurumsal Teklif Talebi',
    corporateProcess: 'Kurumsal Teklif Süreci',
  },
  cookie: {
    title: 'Çerez Tercihleri',
    desc: 'Deneyiminizi iyileştirmek için analitik çerezler kullanıyoruz. Tercihinizi istediğiniz zaman değiştirebilirsiniz.',
    accept: 'Kabul Et',
    reject: 'Reddet',
    settings: 'Ayarlar',
  },
  downloadModal: {
    title: 'Mobil Uygulama',
    desc: "iOS ve Android uygulamaları App Store ve Google Play'de çok yakında.",
    close: 'Kapat',
  },
  defaultPackages: [
    /* Doküman 48: FREE ve FAMILY satıştan kalktı; tek bireysel paket YEARLY.
       Fiziksel kit ayrı ürün (mağaza) — burada paket olarak satılmaz. */
    { id: 'YEARLY', name: 'Yıllık Üyelik', description: 'Yıllık hizmet paketi — PDF etiketinizi basın, dakikalar içinde başlayın.', priceTL: 100, priceUSD: 4, unit: '/ Yıl', badge: 'Bireysel', ctaLabel: 'Hemen Başla', features: ['İlk 2 hafta ücretsiz deneme', 'QR etiket PDF çıktısı — kendin bas, hemen kullan', '2 adede kadar araç ekleme', 'SINIRSIZ push bildirimi', '60 sn içinde okunmayan bildirimlere Yedek SMS', 'Aylık 10 SMS kotası', 'Kasko, trafik sigortası ve muayene hatırlatıcıları', 'Tamamen anonim ve şifreli veri tabanı'], active: true, highlight: true, purchasable: true },
    { id: 'CORPORATE', name: 'Kurumsal Paket', description: 'Şirketler, lojistik filoları ve valeler için tek panel yönetimi.', priceTL: 0, priceUSD: 0, unit: '', badge: 'Kurumsal', ctaLabel: 'Kurumsal Teklif', features: ['Filo büyüklüğünüze göre araç/plaka limiti', 'Dinamik Sürücü Atama & Kaldırma', 'Sürücüye özel akıllı Push & SMS yönlendirmesi', 'Sürücüler için mobil uygulama korumaları', 'Baskı dostu toplu A6 Sticker PDF indirme', 'Detaylı dashboard analitiği'], active: true, highlight: false, purchasable: false },
  ],
  jsonLd: {
    orgDescription: 'Anonim ve güvenli QR tabanlı araç uyarı sistemi. Telefon numarası paylaşmadan, uygulama indirmeden anlık bildirim.',
    appDescription: 'QR kod ile anonim araç uyarısı: push bildirimi, Yedek SMS, KVKK uyumlu şifreleme.',
    offers: [
      { name: 'Ücretsiz', price: '0' },
      { name: 'Standart Üyelik', price: '250' },
      { name: 'Kurumsal Paket', price: '0', description: 'Teklif ile fiyatlandırılır' },
    ],
  },
};

const EN: LandingContent = {
  nav: [
    { href: '#how-it-works', label: 'How It Works' },
    { href: '#features', label: 'Features' },
    { href: '#security', label: 'Security' },
    { href: '#yorumlar', label: 'Reviews' },
    { href: '#faq', label: 'FAQ' },
    { href: '#demo', label: 'Demo' },
    { href: '/magaza', label: 'Store' },
    { href: '/contact', label: 'Contact' },
  ],
  meta: {
    title: 'ParkMesaj — Anonymous & Secure Vehicle Communication',
    description: 'ParkMesaj: anonymous vehicle alerts via QR code. Instant notifications without sharing your phone number or installing an app. Push + SMS fallback, GDPR-ready encryption.',
    ogLocale: 'en_US',
    ogTitle: 'ParkMesaj — Anonymous & Secure Vehicle Alert System',
    ogDescription: 'Warn vehicle owners anonymously in seconds. Scan the QR, pick a template, send instantly.',
    twitterTitle: 'ParkMesaj — Anonymous & Secure Vehicle Alert System',
    twitterDescription: 'Anonymous vehicle alerts via QR. No app download, no phone number sharing.',
    heroImageAlt: 'ParkMesaj QR sticker on a car windshield scanned to deliver a secure parking alert notification',
  },
  navUi: { login: 'Sign In', demo: 'View Demo', menuAria: 'Open or close menu' },
  hero: {
    badge: 'Anonymous · Fast · Secure',
    h1: ['Stay Instantly', 'Informed About', 'Issues With', 'Your Vehicle.'],
    subtitle: 'Without sharing your phone number, without forcing app downloads, and with your identity fully protected — get notified about urgent situations involving your vehicle in seconds.',
    tryFree: 'Try the Demo',
    androidFor: 'For Android',
    androidDownload: 'Google Play',
    iosFor: 'For iOS',
    iosDownload: 'App Store',
    howItWorks: 'How It Works?',
    appsSoon: 'Mobile apps coming soon to the App Store and Google Play',
    scroll: 'Scroll',
  },
  stats: [
    { value: '3 sec', label: 'Average Delivery Time' },
    { value: '100%', label: 'Anonymous & Secure Communication' },
    { value: '0', label: 'App Download Required (for Reporter)' },
  ],
  howItWorks: {
    badge: 'Process',
    title: 'Instant Alerts in 3 Steps',
    subtitle: 'Set up once, stay protected continuously.',
    steps: [
      { num: '01', title: 'Apply the Sticker', desc: 'Place the QR sticker on your windshield. One-time setup for lifetime use.' },
      { num: '02', title: 'QR Code Scan', desc: 'Anyone who notices your vehicle scans the QR with their camera — no app needed — and picks a ready-made template.' },
      { num: '03', title: 'Get Notified Instantly', desc: 'Alerts reach your mobile app or web panel first. If unread within 1 minute, an automatic SMS is sent to your phone.' },
    ],
  },
  features: {
    badge: 'Features',
    titlePrefix: 'Why',
    subtitle: 'Privacy, speed, and security — all at once, without compromise.',
    corporateBadge: 'Enterprise Members',
    corporateTitle: 'Fleet & Company Management Features',
    corporateTag: 'Enterprise Fleet',
    items: [
      { title: 'Full Privacy & Anonymity', desc: 'Your phone number, name, or personal data is never shared with the reporter. Communication stays completely private.' },
      { title: 'Fast & Easy Alerts', desc: 'The person scanning the QR does not need to install an app or sign up. They send their alert in seconds.' },
      { title: 'Smart Notification + SMS Fallback', desc: 'Alerts arrive as push/web notifications first. If unread within 1 minute, the system automatically sends a backup SMS.' },
      { title: 'Photo Evidence System', desc: 'Optional photos can be attached to alerts. EXIF data (location, device) is stripped automatically and permanently deleted after 24 hours.' },
      { title: 'Privacy & Compliance', desc: 'AES-256-GCM encryption, EXIF cleaning, and automatic data retention policies ensure full regulatory compliance.' },
      { title: 'Multi-Vehicle Support', desc: 'Manage 2 vehicles on Individual and 3 on Family from one account, each with its own QR sticker.' },
      { title: 'Dynamic Driver Assignment', desc: 'Assign drivers to fleet vehicles instantly. When drivers change, permissions and routing update without delay.', isCorporate: true },
      { title: 'Bulk A6 PDF Export', desc: 'Download all fleet stickers merged into a single A6 vertical PDF for batch printing.', isCorporate: true },
      { title: 'Smart Alert Routing', desc: 'When QR codes are scanned, alerts go to the assigned driver\'s app and phone (SMS), not the company owner.', isCorporate: true },
      { title: 'Fleet Reports', desc: 'See per-vehicle alert stats and monthly trends right in the enterprise panel.', isCorporate: true },
    ],
  },
  alerts: {
    badge: 'Alert Templates',
    titlePrefix: '4 Ready-Made',
    subtitle: 'After scanning the QR, the reporter taps one button. No free text, no abuse.',
    items: [
      { title: 'Incorrect Parking Alert', desc: '"Your vehicle is parked incorrectly or blocking access — could you please check?"', priority: 'Normal Priority' },
      { title: 'Vehicle Issue Alert', desc: '"Your headlights are on, window is open, or tire looks flat."', priority: 'Normal Priority' },
      { title: 'Minor Damage / Contact Alert', desc: '"There was minor contact or a scrape on your vehicle — can we talk?"', priority: 'High Priority' },
      { title: 'Emergency Alert', desc: '"Emergency situation — towing, suspected theft, or fire!"', priority: 'CRITICAL' },
    ],
  },
  security: {
    badge: 'Security & Privacy',
    titlePrefix: 'Privacy at the',
    titleHighlight: 'Core of Our Design',
    subtitle: 'Built on a Zero-Trust model. Your personal data is never shared with third parties, is stored encrypted, and is permanently deleted after defined retention periods.',
    bars: [
      { label: 'Phone Number Encryption', val: 'AES-256-GCM', pct: 100 },
      { label: 'Automatic Photo Deletion', val: '24 hours', pct: 85 },
      { label: 'EXIF Data Cleaning', val: '100%', pct: 100 },
      { label: 'Two-Way Identity Privacy', val: 'Full', pct: 100 },
    ],
    badges: ['AES-256-GCM Encryption', 'Auto-Delete After 24 Hours', 'EXIF Location Stripping', 'Zero Identity Sharing', 'Privacy Standards Compliant', 'Cloud Bot Protection'],
  },
  roadmap: {
    badge: 'Roadmap',
    titlePrefix: 'Much More',
    soon: 'Coming Soon',
    live: 'Live',
    items: [
      { title: 'License Plate Lookup', desc: 'Send anonymous alerts via plate number even without a QR code.' },
      { title: '15-Min Anonymous Chat', desc: 'Temporary, anonymous, secure chat room between reporter and vehicle owner.', live: true },
      { title: 'Tow Alert Network', desc: 'Instantly warn nearby users when a vehicle is being towed.' },
      { title: 'Mini Store', desc: 'Durable QR sticker and 3D-printed windshield holder kit — order at parkmesaj.com/magaza or in the app.', live: true },
    ],
  },
  store: {
    badge: 'Store',
    title: 'Windshield holder collection',
    subtitle: '3D-printed holders that fix your QR card to the windshield. 10 designs, 4 body colours; ships with a ready QR card and a suction cup. Shipping included, 20 free SMS with every kit.',
    cta: 'See the collection',
    bullets: ['Made to order, ships in 3-5 business days', 'Women, modified-car and premium lines', 'Order in three steps, no account needed'],
  },
  reviews: {
    badge: 'Real Users',
    titlePrefix: 'What Users',
    countSuffix: 'reviews',
    anonymousUser: 'Anonymous User',
  },
  pricing: {
    badge: 'Pricing',
    titlePrefix: 'Transparent Plans',
    subtitle: 'Annual membership with no hidden fees or surprise costs.',
    free: 'Free',
    getQuote: 'Get a Quote',
  },
  membership: {
    badge: 'Membership',
    title: 'How Do I Start?',
    subtitle: 'The individual yearly membership is purchased inside the ParkMesaj app with your App Store or Google Play account. The sticker + holder kit can be ordered from the in-app store as well.',
    individualTitle: 'Individual Users',
    individualDesc: 'Install the app, add your vehicle, print your QR sticker or order the durable sticker + holder kit. The yearly membership starts inside the app; the first 2 weeks are free.',
    download: 'Get the App',
    corporateTitle: 'Fleets and Companies',
    corporateDesc: 'Contact us for the corporate solution with driver assignment, bulk sticker printing and reporting; we will prepare a quote for your needs.',
    corporateCta: 'Request a Quote',
  },
  faq: {
    badge: 'Frequently Asked Questions',
    title: 'Your Questions Answered',
    subtitle: 'Clear answers about ParkMesaj — for search engines and AI assistants.',
    items: [
      { q: 'What is ParkMesaj?', a: 'ParkMesaj is a system that delivers anonymous, secure alerts via a QR code on your vehicle. Your phone number stays off the windshield; reporters send alerts in seconds using ready-made templates without installing an app.' },
      { q: 'Is the reporter\'s identity or location shared?', a: 'No. Alerts are fully anonymous; the reporter\'s phone number or identity is never sent to the vehicle owner. Location is not collected; EXIF (GPS) data in optional photos is automatically removed.' },
      { q: 'Do reporters need to install an app?', a: 'No for reporters — they scan the QR and send alerts via the web. Vehicle owners can use the mobile app or web panel to receive notifications.' },
      { q: 'How do I subscribe and what is included?', a: 'The yearly membership is purchased inside the ParkMesaj app with your App Store or Google Play account; the first 2 weeks are free. It includes up to 2 vehicles, QR sticker PDF export, unlimited push notifications, backup SMS for unread alerts (10 per month) and insurance/inspection reminders. The physical sticker + holder kit is ordered separately from the in-app store.' },
      { q: 'How does backup SMS work?', a: 'Alerts go to push and web panel first. If unread within 1 minute, a backup SMS is automatically sent to the vehicle owner.' },
      { q: 'Is ParkMesaj privacy compliant?', a: 'Yes. Personal data is encrypted with AES-256-GCM, photo metadata is cleaned, and automatic deletion applies on schedule. Details are in our legal pages.' },
      { q: 'Is fleet management supported?', a: 'Yes. The fleet solution is quoted based on your vehicle count and includes dynamic driver assignment, driver-specific push/SMS routing, bulk A6 sticker PDF and dashboard reports. Use the corporate quote form on the home page to get in touch.' },
    ],
  },
  demo: {
    badge: 'Live Demo',
    titlePrefix: 'Try It',
    titleHighlight: 'Now',
    subtitle: 'Scan the test QR code below with your phone camera to instantly experience the alert screen. No additional app required.',
    scanHint: 'Scan with Phone',
    demoQrAlt: 'Demo QR Code',
    phoneAlt: 'Anonymous alert screen opened after QR scan',
    qrNote: '— Test QR code for demo vehicle',
    openLink: 'Open Demo Link',
  },
  footer: {
    tagline: 'Smart communication ecosystem designed to warn vehicle owners anonymously and securely.',
    subtitle: 'Anonymous & Secure Vehicle Alert System',
    product: 'Product',
    legal: 'Legal',
    productLinks: [
      { href: '#how-it-works', label: 'How It Works' },
      { href: '#features', label: 'Features' },
      { href: '#security', label: 'Security' },
      { href: '#faq', label: 'FAQ' },
      { href: '#demo', label: 'Demo' },
      { href: '/contact', label: 'Contact' },
    ],
    legalLinks: [
      { href: '/yasal/kvkk', label: 'Privacy Notice' },
      { href: '/yasal/sozlesme', label: 'Terms of Sale' },
      { href: '/yasal/gizlilik', label: 'Privacy & Cookie Policy' },
      { href: '/yasal/iade', label: 'Cancellation & Refund Policy' },
      { href: '/yasal/sozlesme#teslimat', label: 'Shipping & Delivery Terms' },
    ],
    seller: {
      title: 'Seller',
      name: 'Elektrobil Bilişim Yazılım Elektronik Ar-Ge Reklam ve Gıda San. Tic. Ltd. Şti.',
      address: 'Koca Sinan Mah. Sadık Ahmet Cad. Ahmet Ekşioğlu Apt. No:34 İç Kapı No:19, Merkez / Edirne 22030, Turkey',
      contact: 'destek@parkmesaj.com · +90 544 365 85 25 · Tax ID 3310506351',
    },
    copyright: 'All rights reserved.',
    serviceActive: 'Service Active — pingparking.dpdns.org',
  },
  checkout: {
    corporateSuccess: 'Your enterprise quote request has been recorded. Our sales team will contact you shortly.',
    paymentTitle: 'Payment Details',
    corporateTitle: 'Enterprise Quote Request',
    corporateProcess: 'Enterprise Quote Process',
  },
  cookie: {
    title: 'Cookie Preferences',
    desc: 'We use analytics cookies to improve your experience. You can change your preference anytime.',
    accept: 'Accept',
    reject: 'Reject',
    settings: 'Settings',
  },
  downloadModal: {
    title: 'Mobile App',
    desc: 'iOS and Android apps coming soon to the App Store and Google Play.',
    close: 'Close',
  },
  defaultPackages: [
    /* Doc 48: FREE and FAMILY are gone; single individual plan YEARLY. */
    { id: 'YEARLY', name: 'Yearly Membership', description: 'Yearly service plan — print your PDF sticker and start in minutes.', priceTL: 100, priceUSD: 4, unit: '/ Year', badge: 'Individual', ctaLabel: 'Start Now', features: ['First 2 weeks free', 'QR sticker PDF export — print it yourself, start instantly', 'Up to 2 vehicles', 'UNLIMITED push notifications', 'Backup SMS after 60 sec unread', '10 SMS quota per month', 'Insurance and inspection reminders', 'Fully anonymous encrypted database'], active: true, highlight: true, purchasable: true },
    { id: 'CORPORATE', name: 'Enterprise Plan', description: 'Single-panel management for companies, logistics fleets, and valets.', priceTL: 0, priceUSD: 0, unit: '', badge: 'Enterprise', ctaLabel: 'Enterprise Quote', features: ['Vehicle/plate limit sized to your fleet', 'Dynamic Driver Assignment & Removal', 'Driver-specific Push & SMS routing', 'Mobile app protections for drivers', 'Print-friendly bulk A6 Sticker PDF', 'Detailed dashboard analytics'], active: true, highlight: false, purchasable: false },
  ],
  jsonLd: {
    orgDescription: 'Anonymous and secure QR-based vehicle alert system. Instant notifications without sharing phone numbers or installing apps.',
    appDescription: 'Anonymous vehicle alerts via QR: push notifications, backup SMS, encrypted storage.',
    offers: [
      { name: 'Free', price: '0' },
      { name: 'Standard Membership', price: '12', description: 'USD/year' },
      { name: 'Enterprise Plan', price: '0', description: 'Custom quote-based pricing' },
    ],
  },
};

const LANDING: Record<AppLocale, LandingContent> = { tr: TR, en: EN };

export function getLandingContent(locale: AppLocale, brandName: string): LandingContent {
  const base = LANDING[locale];
  return {
    ...base,
    meta: {
      ...base.meta,
      title: base.meta.title.replace(/ParkMesaj|ParkMesaj/g, brandName),
      ogTitle: base.meta.ogTitle.replace(/ParkMesaj|ParkMesaj/g, brandName),
      twitterTitle: base.meta.twitterTitle.replace(/ParkMesaj|ParkMesaj/g, brandName),
    },
    features: {
      ...base.features,
      titlePrefix: base.features.titlePrefix,
    },
    faq: {
      ...base.faq,
      subtitle: base.faq.subtitle.replace(/ParkMesaj|ParkMesaj/g, brandName),
      items: base.faq.items.map((item) => ({
        q: item.q.replace(/ParkMesaj|ParkMesaj/g, brandName),
        a: item.a.replace(/ParkMesaj|ParkMesaj/g, brandName),
      })),
    },
  };
}

export function buildJsonLd(content: LandingContent, brandName: string, siteUrl: string) {
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        name: brandName,
        url: siteUrl,
        logo: `${siteUrl}/assets/park-mesaj-logo-nobg.png`,
        description: content.jsonLd.orgDescription,
      },
      {
        '@type': 'WebApplication',
        name: brandName,
        url: siteUrl,
        applicationCategory: 'UtilitiesApplication',
        operatingSystem: 'Web, Android, iOS',
        description: content.jsonLd.appDescription,
        offers: content.jsonLd.offers.map((o) => ({
          '@type': 'Offer',
          name: o.name,
          price: o.price,
          priceCurrency: 'TRY',
          ...(o.description ? { description: o.description } : {}),
        })),
      },
      {
        '@type': 'FAQPage',
        mainEntity: content.faq.items.map((item) => ({
          '@type': 'Question',
          name: item.q,
          acceptedAnswer: { '@type': 'Answer', text: item.a },
        })),
      },
    ],
  };
}
